// src/services/feedbackService.ts
import { supabase } from '~/utils/supabaseClient';

export interface FeedbackData {
  session_id: string;
  item_id?: string; // Optional - if provided, feedback is for item; if not, for branch
  feedback: string;
  branch_id: string;
  normalized_rating: number; // Between 0 and 1 (0-1 for 5 stars)
}

export interface UserFeedbackResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Submit user feedback using the insert_user_feedback stored procedure
 * @param feedbackData - The feedback data to submit
 * @returns Promise<UserFeedbackResponse>
 */
export const submitUserFeedback = async (feedbackData: FeedbackData): Promise<UserFeedbackResponse> => {
  try {
    // Validate rating is between 0 and 1
    if (feedbackData.normalized_rating < 0 || feedbackData.normalized_rating > 1) {
      return {
        success: false,
        error: 'Rating must be between 0 and 1'
      };
    }

    // Call the stored procedure - the function expects a JSON parameter
    const { data, error } = await supabase.rpc('insert_user_feedback', {
      payload:feedbackData as any
    });

    if (error) {
      console.error('Error submitting feedback:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit feedback'
      };
    }

    return {
      success: true,
      message: 'Feedback submitted successfully'
    };

  } catch (error) {
    console.error('Unexpected error submitting feedback:', error);
    return {
      success: false,
      error: 'An unexpected error occurred'
    };
  }
};

/**
 * Get session ID from localStorage or generate a fallback
 * @returns string
 */
export const getSessionId = (): string => {
  const sessionId = localStorage.getItem('session_id');
  if (sessionId) {
    return sessionId;
  }
  
  // Fallback: generate a temporary session ID if none exists
  const fallbackId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  console.warn('No session_id found in localStorage, using fallback:', fallbackId);
  return fallbackId;
};

/**
 * Generate a unique session ID for feedback tracking (legacy function - prefer getSessionId)
 * @returns string
 */
export const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Convert star rating (1-5) to normalized rating (0-1)
 * @param starRating - Rating from 1 to 5 stars
 * @returns number between 0 and 1
 */
export const convertStarRatingToNormalized = (starRating: number): number => {
  // Ensure rating is between 1 and 5
  const clampedRating = Math.max(1, Math.min(5, starRating));
  // Convert 1-5 scale to 0-1 scale
  return (clampedRating - 1) / 4;
};

/**
 * Convert normalized rating (0-1) to star rating (1-5)
 * @param normalizedRating - Rating between 0 and 1
 * @returns number between 1 and 5
 */
export const convertNormalizedToStarRating = (normalizedRating: number): number => {
  // Ensure rating is between 0 and 1
  const clampedRating = Math.max(0, Math.min(1, normalizedRating));
  // Convert 0-1 scale to 1-5 scale
  return Math.round(clampedRating * 4 + 1);
};